import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { motion, AnimatePresence } from "framer-motion";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";

import { 
  Users, FileText, UserCheck, AlertCircle, Clock, 
  TrendingUp, BarChart3, PieChart as PieChartIcon 
} from "lucide-react";

export const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/" />;
};

export default function HRDashboardPage() {
  const [stats, setStats] = useState({
    totalStaff: 0,
    activeContracts: 0,
    ongoingRecruitments: 0,
    totalCases: 0,
    pendingLeaveRequests: 0,
  });

  const [growthData, setGrowthData] = useState([]);
  const [leaveTrend, setLeaveTrend] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [disciplineData, setDisciplineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        const [
          statsRes,
          growthRes,
          leaveRes,
          pieRes,
          deptRes,
          disciplineRes,
        ] = await Promise.all([
          fetch( "https://ims.holdthechild.net/api/staff/stats", { headers }),
          fetch( "https://ims.holdthechild.net/api/hr-graphs/monthly-growth", { headers }),
          fetch( "https://ims.holdthechild.net/api/hr-graphs/leave-trends", { headers }),
          fetch( "https://ims.holdthechild.net/api/hr-graphs/leave-status-stats", { headers }),
          fetch( "https://ims.holdthechild.net/api/hr-graphs/department-stats", { headers }),
          fetch( "https://ims.holdthechild.net/api/hr-graphs/disciplinary-trends", { headers }),
        ]);

        const [statsData, growth, leave, pie, dept, discipline] = await Promise.all([
          statsRes.json(),
          growthRes.json(),
          leaveRes.json(),
          pieRes.json(),
          deptRes.json(),
          disciplineRes.json(),
        ]);

        setStats(statsData);
        setGrowthData(Array.isArray(growth) ? growth : []);
        setLeaveTrend(Array.isArray(leave) ? leave : []);
        setPieData(Array.isArray(pie) ? pie : []);
        setDepartmentData(Array.isArray(dept) ? dept : []);
        setDisciplineData(Array.isArray(discipline) ? discipline : []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <Sidebar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="HR" 
                lastWordAccent="Dashboard" 
                subtitle="Centralized human resource metrics and personnel analytics." 
              />
            </div>

            {/* Stats Cards Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
              {[
                { title: "Total Staff", val: stats.totalStaff, icon: Users, accent: "orange", desc: "Overall headcount" },
                { title: "Active Contracts", val: stats.activeContracts, icon: FileText, accent: "blue", desc: "Valid agreements" },
                { title: "Recruitments", val: stats.ongoingRecruitments, icon: UserCheck, accent: "green", desc: "Active hiring" },
                { title: "Cases Reported", val: stats.totalCases, icon: AlertCircle, accent: "red", desc: "Disciplinary actions" },
                { title: "Pending Leave", val: stats.pendingLeaveRequests, icon: Clock, accent: "amber", desc: "Awaiting approval" }
              ].map((stat, i) => (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  key={i}
                >
                  <PremiumStatCard
                    title={stat.title}
                    value={stat.val}
                    icon={<stat.icon className={`text-${stat.accent}-600`} />}
                    iconBg={`bg-${stat.accent}-50`}
                    description={stat.desc}
                  />
                </motion.div>
              ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <PremiumCard title="Staff Growth Trend" icon={<TrendingUp size={18} />}>
                <div className="py-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={growthData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }} />
                      <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={4} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6, strokeWidth: 0 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </PremiumCard>

              <PremiumCard title="Leave Allocation Mix" icon={<PieChartIcon size={18} />}>
                <div className="py-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Array.isArray(pieData) ? pieData : []}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={65}
                        paddingAngle={8}
                      >
                        {(Array.isArray(pieData) ? pieData : []).map((entry, index) => {
                          const COLORS = ["#f97316", "#fbbf24", "#10b981", "#3b82f6", "#ef4444", "#8b5cf6"];
                          return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={10} />;
                        })}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </PremiumCard>

              <PremiumCard title="Leave Engagement Trends" icon={<Clock size={18} />}>
                <div className="py-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={leaveTrend}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                      <Line type="monotone" dataKey="leaveRequests" stroke="#10b981" strokeWidth={4} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </PremiumCard>

              <PremiumCard title="Departmental Force Strenth" icon={<BarChart3 size={18} />}>
                <div className="py-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={departmentData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="department" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} hide={departmentData.length > 5} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                      <Bar dataKey="count" fill="#fbbf24" radius={[8, 8, 0, 0]} barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </PremiumCard>

              <PremiumCard title="Disciplinary Incident Analytics" icon={<AlertCircle size={18} />} className="lg:col-span-2">
                <div className="py-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={disciplineData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                      <Bar dataKey="cases" fill="#ef4444" radius={[8, 8, 0, 0]} barSize={60} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </PremiumCard>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

